package com.evoapps.evoshift.domain.model

import kotlinx.datetime.DayOfWeek
import kotlinx.datetime.LocalDate
import kotlinx.datetime.isoDayNumber

/*
 * SH-121: opening, closing and custom checklists. Managers keep the recipes per workplace
 * (workplaces/{id}/checklist_templates); the person on a shift ticks a copy pinned to that shift
 * (shift_checklists/{shiftId}_{templateId}), which keeps the items as they were that day, like the
 * roster's cost snapshots. functions/checklists.js applies the same rules (which shift gets which
 * checklist) for the reminder push: keep both in step.
 */

/** What a checklist is for; the reminder push is about [CLOSING] ones only. */
enum class ChecklistKind {
    OPENING,
    CLOSING,
    CUSTOM;

    companion object {
        /** Tolerant lookup: unknown or missing means [CUSTOM]. */
        fun fromName(name: String?): ChecklistKind = entries.firstOrNull { it.name == name } ?: CUSTOM
    }
}

/** One task. [requiresNote]: ticking it needs a few words (a fridge temperature, the float counted). */
data class ChecklistItem(
    val id: String,
    val text: String,
    val requiresNote: Boolean = false
)

/**
 * A manager's checklist recipe. It goes on shifts matching one of [shiftTemplateIds] (same planned
 * start and end as the shift template) and on the days of [weekdays] (ISO, 1 = Monday): there an
 * opening checklist goes to whoever starts first, a closing one to whoever finishes last and a
 * custom one to everybody working that day.
 */
data class ChecklistTemplate(
    val id: String,
    val workplaceId: String,
    val name: String,
    val kind: ChecklistKind,
    val items: List<ChecklistItem>,
    val shiftTemplateIds: List<String> = emptyList(),
    val weekdays: List<Int> = emptyList(),
    val updatedAt: String? = null,
    val updatedBy: String? = null
)

/** An item's state on a shift's checklist. [at]: when it was ticked (instant, ISO). */
data class ChecklistCheck(
    val done: Boolean,
    val note: String? = null,
    val at: String? = null
)

/**
 * A checklist on one shift: the template's items as they were when the person first ticked, and
 * what they ticked. Not stored until the first tick ([updatedAt] null).
 */
data class ShiftChecklist(
    val shiftId: String,
    val templateId: String,
    val workplaceId: String,
    val rosterId: String,
    val date: String,
    val userId: String,
    val name: String,
    val kind: ChecklistKind,
    val items: List<ChecklistItem>,
    val checks: Map<String, ChecklistCheck> = emptyMap(),
    val completedAt: String? = null,
    val updatedAt: String? = null,
    /** SH-127: when the person went on without finishing it on the clock-in tablet (instant, ISO). */
    val skippedAt: String? = null,
    /** SH-127: where it was skipped ("KIOSK"); only the server records a skip. */
    val skippedVia: String? = null
) {
    val id: String get() = idOf(shiftId, templateId)
    val doneCount: Int get() = items.count { checks[it.id]?.done == true }
    val isComplete: Boolean get() = items.isNotEmpty() && doneCount == items.size
    val isStarted: Boolean get() = doneCount > 0
    fun isDone(itemId: String): Boolean = checks[itemId]?.done == true
    fun noteOf(itemId: String): String? = checks[itemId]?.note?.takeIf { it.isNotBlank() }

    companion object {
        fun idOf(shiftId: String, templateId: String) = "${shiftId}_$templateId"

        /** A fresh, unticked checklist of [template] for [shift] (assigned to [userId]). */
        fun fresh(template: ChecklistTemplate, shift: Shift, userId: String) = ShiftChecklist(
            shiftId = shift.id,
            templateId = template.id,
            workplaceId = template.workplaceId,
            rosterId = shift.rosterId,
            date = shift.date,
            userId = userId,
            name = template.name,
            kind = template.kind,
            items = template.items
        )
    }
}

/** Why a checklist template or a tick cannot be saved (the screens say it in words). */
enum class ChecklistProblem {
    NO_NAME,
    NO_ITEMS,
    EMPTY_ITEM,
    TOO_MANY_ITEMS,
    TOO_LONG,
    NOT_ATTACHED,
    NOTE_REQUIRED
}

class InvalidChecklistException(val problems: Set<ChecklistProblem>) :
    IllegalArgumentException("Invalid checklist: ${problems.joinToString()}")

object Checklists {
    const val MAX_ITEMS = 40
    const val NAME_MAX_LENGTH = 80
    const val ITEM_MAX_LENGTH = 200
    const val NOTE_MAX_LENGTH = 500

    /** What is wrong with [template] (empty: it can be saved). */
    fun problems(template: ChecklistTemplate): Set<ChecklistProblem> = buildSet {
        if (template.name.isBlank()) add(ChecklistProblem.NO_NAME)
        if (template.name.length > NAME_MAX_LENGTH) add(ChecklistProblem.TOO_LONG)
        if (template.items.isEmpty()) add(ChecklistProblem.NO_ITEMS)
        if (template.items.any { it.text.isBlank() }) add(ChecklistProblem.EMPTY_ITEM)
        if (template.items.any { it.text.length > ITEM_MAX_LENGTH }) add(ChecklistProblem.TOO_LONG)
        if (template.items.size > MAX_ITEMS) add(ChecklistProblem.TOO_MANY_ITEMS)
        if (template.shiftTemplateIds.isEmpty() && template.weekdays.isEmpty()) add(ChecklistProblem.NOT_ATTACHED)
    }

    /** "HH:mm" of a stored local date-time ("2026-02-01T08:00[:00]") or of a template time ("08:00"). */
    fun hourOf(value: String): String = value.substringAfter('T').take(5)

    /** Whether [shift] was planned like [template] (same start and end hour). */
    fun matches(shift: Shift, template: ShiftTemplate): Boolean =
        hourOf(shift.plannedTime.start) == hourOf(template.startTime) && hourOf(shift.plannedTime.end) == hourOf(template.endTime)

    /** ISO day of week (1 = Monday) of an ISO date; null when it is not one. */
    fun weekdayOf(date: String): Int? = runCatching { LocalDate.parse(date.take(10)).dayOfWeek.isoDayNumber }.getOrNull()

    /**
     * The checklists of [shift] among [templates]: by its shift template, or by its day. [dayShifts]
     * are the workplace's shifts that day (the shift among them); only assigned ones open or close.
     */
    fun forShift(
        shift: Shift,
        dayShifts: List<Shift>,
        templates: List<ChecklistTemplate>,
        shiftTemplates: List<ShiftTemplate>
    ): List<ChecklistTemplate> {
        if (shift.userId == null) return emptyList()
        val matching = shiftTemplates.filter { matches(shift, it) }.map { it.id }.toSet()
        val weekday = weekdayOf(shift.date)
        val working = (dayShifts.filter { it.date == shift.date && it.userId != null } + shift).distinctBy { it.id }
        val opens = working.minOf { startKey(it) } == startKey(shift)
        val closes = working.maxOf { endKey(it) } == endKey(shift)
        return templates.filter { template ->
            template.shiftTemplateIds.any { it in matching } ||
                (weekday != null && weekday in template.weekdays && when (template.kind) {
                    ChecklistKind.OPENING -> opens
                    ChecklistKind.CLOSING -> closes
                    ChecklistKind.CUSTOM -> true
                })
        }.sortedWith(compareBy<ChecklistTemplate>({ it.kind.ordinal }, { it.name }))
    }

    private fun startKey(shift: Shift) = shift.plannedTime.start.take(16)
    private fun endKey(shift: Shift) = shift.plannedTime.end.take(16)

    /** Every day of the week, Monday first, as stored in [ChecklistTemplate.weekdays]. */
    val WEEKDAYS: List<Int> = DayOfWeek.entries.map { it.isoDayNumber }
}
